import React, { useEffect, useState } from 'react';
import { Alert, Button } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import colors from 'components/shared/colors';
import api from 'services/api';
import { getCompanyID, getUser } from 'redux/selectors';

export const ACTIVE_REMOTE_SESSION_KEY = 'activeRemoteSession';
const LAST_STALE_CHECK_KEY = 'activeRemoteSessionCheckedAt';
const STALE_CHECK_THROTTLE_MS = 60000;

export const logPagePath = aircraftLogID => `/data/aircraft/logs/${aircraftLogID}`;

export const readActiveRemoteSession = () => {
  try {
    const raw = localStorage.getItem(ACTIVE_REMOTE_SESSION_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

export const writeActiveRemoteSession = session => {
  try {
    if (session) {
      localStorage.setItem(ACTIVE_REMOTE_SESSION_KEY, JSON.stringify(session));
    } else {
      localStorage.removeItem(ACTIVE_REMOTE_SESSION_KEY);
      sessionStorage.removeItem(LAST_STALE_CHECK_KEY);
    }
  } catch (e) {
    // 
  }
};

const bannerStyle = {
  marginBottom: '1rem',
  fontSize: '1.5rem',
  borderWidth: '5px',
  borderStyle: 'dashed',
  borderColor: colors.orange,
  position: 'sticky',
  top: '0',
  zIndex: '100',
  marginLeft: '-30px',
  marginRight: '-30px',
  marginTop: '-30px',
};

const RemoteSessionBanner = ({ registration, logLabel, action, showCommitHint = true }) => {
  const target = [registration, logLabel].filter(Boolean).join(' — ');

  return (
    <Alert
      message={`Remote Session In Progress${target ? ` on ${target}` : ''}.${
        showCommitHint ? ' Your changes will not be entered into the log until you commit them.' : ''
      }`}
      type="warning"
      size="large"
      closable={false}
      banner
      showIcon
      style={bannerStyle}
      action={action}
    />
  );
};

export const GlobalRemoteSessionBanner = () => {
  const location = useLocation();
  const companyID = useSelector(getCompanyID);
  const user = useSelector(getUser);
  const [session, setSession] = useState(readActiveRemoteSession);

  useEffect(() => {
    setSession(readActiveRemoteSession());
  }, [location.pathname]);

  useEffect(() => {
    if (!session || !user?.id) {
      return;
    }
    if (session.startedByUserID && session.startedByUserID !== user.id) {
      writeActiveRemoteSession(null);
      setSession(null);
    }
  }, [session, user?.id]);

  useEffect(() => {
    if (!session?.sessionID || !companyID) {
      return undefined;
    }

    let lastChecked = 0;
    try {
      lastChecked = Number(sessionStorage.getItem(LAST_STALE_CHECK_KEY)) || 0;
    } catch (e) {
      lastChecked = 0;
    }
    if (Date.now() - lastChecked < STALE_CHECK_THROTTLE_MS) {
      return undefined;
    }

    let cancelled = false;
    api
      .get(`/v1/aircraft/company/${companyID}/open_remote_access_sessions`)
      .then(response => {
        if (cancelled) return;
        try {
          sessionStorage.setItem(LAST_STALE_CHECK_KEY, String(Date.now()));
        } catch (e) {
          // ignore
        }
        const openSessions = response?.data?.Data || [];
        const stillOpen = openSessions.some(s => s.ID === session.sessionID);
        if (!stillOpen) {
          writeActiveRemoteSession(null);
          setSession(null);
        }
      })
      .catch(() => {
      });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, session?.sessionID, companyID]);

  if (!session?.aircraftLogID) {
    return null;
  }

  if (location.pathname === logPagePath(session.aircraftLogID)) {
    return null;
  }

  return (
    <RemoteSessionBanner
      registration={session.registration}
      logLabel={session.logLabel}
      action={
        <Link to={logPagePath(session.aircraftLogID)}>
          <Button size="large" type="primary">
            Return to log
          </Button>
        </Link>
      }
    />
  );
};

export default RemoteSessionBanner;
